import jsPDF from 'jspdf';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import axios from 'axios';
import Swal from 'sweetalert2';

// We need the API_BASE to construct the upload URL
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/';

const isInAppMode = () => {
  const isStandalone = window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone;
  const isCapacitor = Boolean(window.Capacitor?.isNativePlatform?.() || window.Capacitor);
  return Boolean(isStandalone || isCapacitor);
};

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const buildQuotationHtmlDocument = (quotation, items = []) => {
  const rows = items.map((item, idx) => `
    <tr>
      <td>${idx + 1}</td>
      <td>${escapeHtml(item.product_name || '-')}</td>
      <td>${escapeHtml(item.quantity || 0)}</td>
      <td>${escapeHtml(item.unit_price || 0)}</td>
      <td>${escapeHtml(item.total_price || 0)}</td>
    </tr>
  `).join('');

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <title>Quotation ${escapeHtml(quotation.quotation_number)}</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 16px; color: #222; }
    h1 { margin: 0 0 16px; font-size: 20px; }
    p { margin: 6px 0; }
    table { width: 100%; border-collapse: collapse; margin-top: 16px; }
    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
    th { background: #f5f5f5; }
  </style>
</head>
<body>
  <h1>Quotation ${escapeHtml(quotation.quotation_number)}</h1>
  <p><strong>Customer:</strong> ${escapeHtml(quotation.customer_name || '-')}</p>
  <p><strong>Email:</strong> ${escapeHtml(quotation.customer_email || '-')}</p>
  <p><strong>Phone:</strong> ${escapeHtml(quotation.customer_phone || '-')}</p>
  <p><strong>Total:</strong> ₹${escapeHtml(quotation.total_amount || 0)}</p>
  <table>
    <thead>
      <tr>
        <th>#</th>
        <th>Product</th>
        <th>Qty</th>
        <th>Unit Price</th>
        <th>Total</th>
      </tr>
    </thead>
    <tbody>
      ${rows || '<tr><td colspan="5">No items</td></tr>'}
    </tbody>
  </table>
</body>
</html>`;
};

export const generateQuotationPDF = async (quotation, items) => {
  try {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let y = 12;

    const set = (size = 11, style = 'normal') => { pdf.setFont('helvetica', style); pdf.setFontSize(size); pdf.setLineHeightFactor(1.15); };
    const text = (t, x, y2, opts = {}) => {
      if (Array.isArray(t)) return pdf.text(t, x, y2, opts);
      return pdf.text(String(t ?? ''), x, y2, opts);
    };
    const line = (x1, y1, x2, y2) => pdf.line(x1, y1, x2, y2);
    const money = (a) => {
      try {
        const s = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(a || 0));
        return `Rs. ${String(s).replace(/[\s\u00A0\u200B-\u200D\u202F\uFEFF]/g, '')}`; 
      } catch { return `Rs. ${Number(a || 0).toFixed(2)}`; }
    };

    // Helper: right-align without using jsPDF align (prevents per-char spacing on some viewers)
    const textRight = (t, xRight, y2) => {
      const str = String(t ?? '');
      const w = pdf.getTextWidth(str);
      pdf.text(str, xRight - w, y2);
    };
    const dateStr = (d) => new Date(d).toLocaleDateString('en-IN');

    // Outer border (to mimic printed template)
    pdf.setDrawColor(0); pdf.setLineWidth(0.4);
    pdf.rect(12, 10, pageWidth - 24, pageHeight - 20);

    // Try to draw company logo (favicon or venzo-logo)
    const loadDataUrl = async (src) => {
      try {
        const res = await fetch(src);
        const blob = await res.blob();
        return await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      } catch {
        return null;
      }
    };

    const logoData = (await loadDataUrl('/venzo-logo.png')) || (await loadDataUrl('/favicon.ico'));
    const headerLeft = 14;
    const headerTop = 14; // pushed down a bit
    if (logoData) {
      try { pdf.addImage(logoData, 'PNG', headerLeft, headerTop, 20, 20); } catch { /* ignore */ }
    }

    // Header texts
    const textX = logoData ? headerLeft + 26 : headerLeft;
    set(13, 'bold');
    text('VENZO WOOD PRIVATE LIMITED', textX, headerTop + 4);
    set(9, 'normal');
    text('Andheri, Lower Parel, Surat, Nashik, Hyderabad', textX, headerTop + 10);
    set(10, 'bold');
    text('+918080282228', pageWidth - 14 - pdf.getTextWidth('+918080282228'), headerTop + 4);
    
    y = headerTop + 26; // Add space below logo
    pdf.setDrawColor(200, 200, 200); pdf.setLineWidth(0.3);
    line(14, y, pageWidth - 14, y);
    y += 8;

    // Title
    set(14, 'bold');
    text('QUOTATION', 14, y);
    
    // Date and Ref on the right side
    set(10, 'normal');
    textRight(`Date: ${dateStr(quotation.created_at)}`, pageWidth - 14, y - 4);
    textRight(`Ref No.: ${quotation.quotation_number}`, pageWidth - 14, y + 2);
    
    y += 6;
    
    // Grid for Meta & Customer Details
    set(10, 'bold'); text('Project Details', 14, y); 
    set(10, 'bold'); text('Customer Details', 105, y); 
    y += 6;
    
    const startY = y;
    let leftY = startY;
    let rightY = startY;

    set(9, 'normal');
    const printLeft = (lbl, val) => {
      set(9, 'bold'); text(lbl, 14, leftY);
      set(9, 'normal'); text(String(val || '-'), 42, leftY);
      leftY += 6;
    };
    
    const printRight = (lbl, val, isMultiline = false) => {
      set(9, 'bold'); text(lbl, 105, rightY);
      set(9, 'normal');
      if (isMultiline) {
        const lines = pdf.splitTextToSize(String(val || '-'), 65);
        text(lines, 130, rightY);
        rightY += 5 * lines.length + 1;
      } else {
        text(String(val || '-'), 130, rightY);
        rightY += 6;
      }
    };

    // Meta (sales/prepared/project/confirmed)
    printLeft('Sales:', quotation.sales || '-');
    printLeft('Prepared By:', quotation.prepared_by || '-');
    printLeft('Project:', quotation.project || '-');
    printLeft('Confirmed By:', quotation.confirmed_by || '-');
    
    // Customer
    printRight('Client Name:', quotation.customer_name);
    printRight('Email ID:', quotation.customer_email);
    printRight('Tel:', quotation.customer_phone);
    printRight('GSTIN No:', quotation.customer_gstin);
    printRight('Arch Name:', quotation.architect_name);
    printRight('Address:', quotation.customer_address, true);

    y = Math.max(leftY, rightY) + 6;

    // Items table
    const col = (arr) => arr.reduce((a, c) => a + c, 0);
    const cols = [12, 22, 68, 20, 28, 32]; // Sum = 182, fits inside A4 (210mm - 28 left/right margins total)
    const x0 = 14; let x = x0; const rowH = 9;

    // Table header box
    pdf.setDrawColor(120); pdf.setLineWidth(0.2);
    set(10, 'bold');
    const headers = ['Ref No', 'Qty in Sheet', 'Description', 'Item', 'Price Per Sqft', 'Total Price'];
    // top header row
    x = x0; headers.forEach((h, i) => { text(h, x + 2, y + 5); x += cols[i]; });
    // header outline
    line(x0, y, x0 + col(cols), y); // top
    line(x0, y + rowH, x0 + col(cols), y + rowH); // bottom of header
    // verticals header
    x = x0; cols.forEach((w) => { line(x, y, x, y + rowH); x += w; }); line(x0 + col(cols), y, x0 + col(cols), y + rowH);
    y += rowH;

    // rows
    set(10, 'normal');
    items.forEach((it, idx) => {
      if (y > pageHeight - 50) { pdf.addPage(); y = 20; }
      x = x0;
      const row = [String(idx + 1), String(it.quantity || ''), it.product_name || '', '—', money(it.unit_price), money(it.total_price)];
      // cells with proper alignment to avoid overlap
      text(row[0], x + 2, y + 6); x += cols[0];
      textRight(row[1], x + cols[1] - 2, y + 6); x += cols[1];
      text(row[2], x + 2, y + 6); x += cols[2];
      textRight(row[3], x + cols[3] - 2, y + 6); x += cols[3];
      textRight(row[4], x + cols[4] - 2, y + 6); x += cols[4];
      textRight(row[5], x + cols[5] - 2, y + 6);
      // row box
      line(x0, y, x0 + col(cols), y);
      line(x0, y + rowH, x0 + col(cols), y + rowH);
      x = x0; cols.forEach((w) => { line(x, y, x, y + rowH); x += w; }); line(x0 + col(cols), y, x0 + col(cols), y + rowH);
      y += rowH + 0.5;
    });

    // Total row
    if (y > pageHeight - 40) { pdf.addPage(); y = 20; }
    x = x0;
    // box
    line(x0, y, x0 + col(cols), y);
    line(x0, y + rowH, x0 + col(cols), y + rowH);
    x = x0; cols.forEach((w) => { line(x, y, x, y + rowH); x += w; }); line(x0 + col(cols), y, x0 + col(cols), y + rowH);
    set(11, 'bold');
    text('Total', x0 + col(cols) - cols[5] - 14, y + 6);
    textRight(money(quotation.total_amount), x0 + col(cols) - 2, y + 6);
    y += rowH + 6;

    // Terms
    const terms = [
      'Rates are delivery ex mumbai',
      'Payment 100% advance. Delivery in IMM after receipt of full payment for fluting.',
      'Any changes in Government Policy will be applicable at time of delivery.',
      'Goods once sold should not be taken back. Orders placed cannot be cancelled or exchanged.',
      'Quality inspection at our end shall be treated as final.',
      'Issue a Purchase Order to organise delivery of the goods.',
      'Issue a cheque for 100% of the amount in favour of VENZO WOOD PRIVATE LIMITED.',
      'As veneers are natural items no guarantee / warranty for colour & figure variations.',
      'Any dispute subject to mumbai jurisdiction.',
      'Our Delivery Ex-warehouse',
      'Kindly note our GST No. 27AAHCV3921F1ZC'
    ];

    set(10);
    terms.forEach((t) => { if (y > pageHeight - 20) { pdf.addPage(); y = 20; } pdf.text(t, x0, y); y += 6; });

    // Bank details
    if (y > pageHeight - 60) { pdf.addPage(); y = 20; }
    set(11, 'bold'); text('Bank details', x0, y); y += 6; set(10);
    const bank = [
      ['Company Name', 'VENZO WOOD PRIVATE LIMITED'],
      ['Bank', 'HDFC Bank'],
      ['Account Number', '50200093579707'],
      ['IBAN Number', 'HDFC0000668'],
      ['BRANCH', 'Vile Parla East'],
      ['Currency', 'INR']
    ];
    bank.forEach(([k, v]) => { text(`${k}  ${v}`, x0, y); y += 6; });

    return pdf;
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

export const downloadQuotationPDF = async (quotation, items) => {
  try {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const pdf = await generateQuotationPDF(quotation, items);
    const fileName = `quotation-${quotation.quotation_number}.pdf`;

    // 1. Capacitor Native APKs
    if (Capacitor.isNativePlatform() && Filesystem && Share) {
      try {
        const base64Data = btoa(pdf.output());
        const savedFile = await Filesystem.writeFile({
          path: fileName,
          data: base64Data,
          directory: Directory.Cache
        });

        await Share.share({
          title: `Quotation ${quotation.quotation_number}`,
          url: savedFile.uri,
          dialogTitle: 'Share or Save PDF'
        });
        return true;
      } catch (nativeError) {
        console.error('Native SDK PDF export failed:', nativeError);
      }
    }

    // 2. Mobile WebBrowsers / PWAs (Android "Add to Home Screen")
    // Generating PDFs as Blobs and downloading them directly via HTML5 download attribute
    // crashes Android WebAPKs (Out-Of-Memory / silent reload). 
    // Just like the E-Catalogue, we upload the PDF to the server and cleanly download the HTTPS static file.
    if (isMobile || isInAppMode()) {
      // Convert jsPDF to Blob
      const pdfBlob = pdf.output('blob');
      
      const formData = new FormData();
      formData.append('file', pdfBlob, fileName);

      // We use axios directly since pdfGenerator is outside React Context
      const uploadUrl = API_BASE.endsWith('/') ? `${API_BASE}upload/file` : `${API_BASE}/upload/file`;
      
      const response = await axios.post(uploadUrl, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data && response.data.url) {
        // EXACT E-Catalogue Download Logic?
        // Actually, if the <a download> forces a broken notification in WebAPK context, 
        // the safest way to "download and view" is to just open the explicit HTTPS file URL 
        // in a new tab. Mobile OS will automatically invoke proper File Viewers or prompt a clean download.
        window.open(response.data.url, '_blank');
        return true;
      }
    }

    // 3. Desktop Browsers
    // Sufficient RAM to safely save Blob natively
    pdf.save(fileName);
    return true;

  } catch (error) {
    console.error('Error downloading PDF:', error);
    throw error;
  }
};
